import React, { useContext, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { AlarmRecordsAnalysisContext } from '../..';
import { apiV2AlarmRecordAnalysisAlarmDurationRankingPost } from '@maxtropy/device-customer-apis-v2';
import { ColorMap, ColorRankMap, DateType } from '../../const';
import styles from './index.module.scss';
import classNames from 'classnames';
import { Button, Empty, Tag, Tooltip } from '@maxtropy/components';
import { AlarmLevel, AlarmLevelColorDisplay, AlarmLevelDisplay, AlarmState, AlarmStateDisplay } from '@/shared/types';
import CardBox from '../CardBox';
import dayjs from 'dayjs';
import { formatDuration, getDefaultTime } from '../../utils';
import { Spin } from 'antd';
import qs from 'qs';
import { getOuIdsByDeviceId } from '@/api/device';
import light_line from '../../imgs/light_line.png';
import { ClockCircleOutlined } from '@ant-design/icons';

const AlarmTimesRanking = () => {
  const { timeResolution, value, selectedSceneId, activeKey } = useContext(AlarmRecordsAnalysisContext);

  // 持续报警时长排名
  const { data: alarmTimesRankingList, loading } = useRequest(
    async () => {
      return await apiV2AlarmRecordAnalysisAlarmDurationRankingPost({
        sceneId: selectedSceneId,
        state: activeKey === 'all' ? undefined : activeKey,
        timeResolution: timeResolution === DateType.CUSTOM ? 'D' : timeResolution,
        startTime: getDefaultTime(timeResolution!, value?.[0]).startTime,
        endTime: getDefaultTime(timeResolution!, value?.[1]).endTime,
      });
    },
    {
      ready: !!timeResolution && !!value && !!activeKey,
      refreshDeps: [timeResolution, selectedSceneId, value, activeKey],
    }
  );

  const isEmpty = useMemo(() => {
    return !alarmTimesRankingList || alarmTimesRankingList?.list?.length === 0;
  }, [alarmTimesRankingList]);

  return (
    <CardBox title="持续报警时长排名">
      <Spin spinning={loading}>
        {isEmpty ? (
          <Empty />
        ) : (
          <div className={styles.alarm_container}>
            {(alarmTimesRankingList?.list ?? []).map((item, index) => (
              <div className={styles.alarm_item} key={index}>
                {/* 左侧序号与等级颜色 */}
                <div className={styles.alarm_left}>
                  <div
                    className={styles.alarm_index}
                    style={{ backgroundColor: index > 2 ? '#5D7092' : ColorRankMap[index] }}
                  >
                    {index + 1}
                  </div>
                </div>

                {/* 中间内容 */}
                <div className={styles.alarm_content}>
                  <div className={styles.alarm_title_row}>
                    <Tag border="solid" color={AlarmLevelColorDisplay[item.level as AlarmLevel]}>
                      {AlarmLevelDisplay[item.level as AlarmLevel]}
                    </Tag>
                    <div className={styles.alarm_title}>{item.alarmInfo}</div>
                  </div>

                  <div className={styles.alarm_time}>
                    <div className={styles.alarm_time_row}>
                      报警时间: {item.alarmTime ? dayjs(item.alarmTime).format('YYYY-MM-DD HH:mm:ss') : '--'}
                    </div>
                    <div
                      className={classNames(
                        styles.alarm_status,
                        item.state === AlarmState.RECOVER ? styles.status_ok : styles.status_bad
                      )}
                    >
                      {AlarmStateDisplay[item.state as AlarmState]}
                    </div>
                    {item.state === AlarmState.RECOVER && (
                      <div className={styles.recovery_time_row}>
                        <Tooltip
                          title={
                            <>
                              恢复时间:
                              {item.recoveryTime ? dayjs(item.recoveryTime).format('YYYY-MM-DD HH:mm:ss') : '--'}
                            </>
                          }
                        >
                          <ClockCircleOutlined style={{ color: '#59D744' }} />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>

                {/* 右侧：持续时间 + 操作 */}
                <div className={styles.alarm_right}>
                  <div className={styles.alarm_duration}>{formatDuration(item.duration ?? 0)}</div>
                  <div className={styles.alarm_btn_row}>
                    <Button
                      type="link"
                      className={classNames(styles.alarm_btn, styles.blue)}
                      onClick={() => {
                        getOuIdsByDeviceId(item.deviceId!).then(res => {
                          let ids = res.map(item => item.id);
                          window.open(
                            `${window.ALARMWORKORDER}/orderv2/order/add/index?${qs.stringify(
                              {
                                problemSource: 10,
                                operationUnitCode: (ids ?? []).join(','),
                                alarmId: item.id,
                                alarmTypeCode: -1,
                              },
                              { indices: false }
                            )}`,
                            '_blank'
                          );
                        });
                      }}
                    >
                      发起工单
                    </Button>
                    <Button
                      type="link"
                      className={classNames(styles.alarm_btn, styles.gray)}
                      onClick={() => {
                        window.open(`/device/alarm/record/detail/${item.id}`, '_blank');
                      }}
                    >
                      详情
                    </Button>
                  </div>
                </div>

                <img className={styles.bottom_line} src={light_line} alt="" />
              </div>
            ))}
          </div>
        )}
      </Spin>
    </CardBox>
  );
};

export default AlarmTimesRanking;
